import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import DischargeConfirmationDialog from "@/components/Patient/DischargeConfirmationDialog";

import query from "@/Utils/request/query";
import { cn } from "@/lib/utils";
import {
  ENCOUNTER_ADMIT_SOURCE,
  ENCOUNTER_ARRIVAL_MODE,
  ENCOUNTER_DIET_PREFERENCE,
  ENCOUNTER_DISCHARGE_DISPOSITION,
  ENCOUNTER_PRIORITY,
  EncounterStatus,
  type EncounterAdmitSources,
  type EncounterArrivalMode,
  type EncounterClass,
  type EncounterDietPreference,
  type EncounterDischargeDisposition,
  type EncounterEdit,
  type EncounterPriority,
  type EncounterRead,
} from "@/types/emr/encounter/encounter";
import encounterApi from "@/types/emr/encounter/encounterApi";
import { QuestionValidationError } from "@/types/questionnaire/batch";
import type {
  QuestionnaireResponse,
  ResponseValue,
} from "@/types/questionnaire/form";
import type { Question } from "@/types/questionnaire/question";
import { useFieldError } from "@/types/questionnaire/validation";
import careConfig from "@careConfig";

import { ENCOUNTER_FIELDS } from "./encounterQuestionValidation";

interface EncounterQuestionProps {
  question: Question;
  encounterId: string;
  questionnaireResponse: QuestionnaireResponse;
  updateQuestionnaireResponseCB: (
    values: ResponseValue[],
    questionId: string,
    note?: string,
  ) => void;
  disabled?: boolean;
  clearError: () => void;
  organizations?: string[];
  patientId?: string;
  facilityId: string;
  errors?: QuestionValidationError[];
}

const toDateTimeLocal = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
};

const toISOString = (value: string) =>
  value ? new Date(value).toISOString() : undefined;

export function EncounterQuestion({
  questionnaireResponse,
  updateQuestionnaireResponseCB,
  disabled,
  clearError,
  encounterId,
  facilityId,
  errors = [],
}: EncounterQuestionProps) {
  // Fetch encounter data
  const { data: encounterData, isLoading } = useQuery({
    queryKey: ["encounter", encounterId],
    queryFn: query(encounterApi.get, {
      pathParams: { id: encounterId },
      queryParams: { facility: facilityId },
    }),
    enabled: !!encounterId,
  });
  const { t } = useTranslation();
  const { hasError, getError } = useFieldError(
    questionnaireResponse.question_id,
    errors,
  );

  const [encounter, setEncounter] = useState<EncounterEdit>({
    status: EncounterStatus.UNKNOWN,
    encounter_class: careConfig.defaultEncounterType,
    period: {
      start: new Date().toISOString(),
      end: undefined,
    },
    priority: "routine",
    external_identifier: "",
    hospitalization: {
      re_admission: false,
      admit_source: "other",
      discharge_disposition: careConfig.defaultDischargeDisposition,
      diet_preference: "none",
    },
    discharge_summary_advice: null,
  });

  useEffect(() => {
    if (
      encounter.status === "discharged" ||
      encounter.status === "completed" ||
      encounter.status === "cancelled" ||
      encounter.status === "discontinued" ||
      encounter.status === "entered_in_error"
    ) {
      if (!encounter.period.end) {
        handleUpdateEncounter({
          period: {
            ...encounter.period,
            end: new Date().toISOString(),
          },
        });
      }
    } else {
      handleUpdateEncounter({
        period: {
          ...encounter.period,
          end: undefined,
        },
      });
    }
  }, [encounter.status]);

  // Transform EncounterRead to EncounterEdit format
  const transformEncounterForUpdate = (
    read: EncounterRead,
  ): Partial<EncounterEdit> => {
    return {
      status: read.status,
      encounter_class: read.encounter_class,
      period: read.period,
      priority: read.priority,
      hospitalization: read.hospitalization,
      external_identifier: read.external_identifier,
      discharge_summary_advice: read.discharge_summary_advice,
    };
  };

  // Update encounter state when data is loaded
  useEffect(() => {
    if (encounterData) {
      handleUpdateEncounter(transformEncounterForUpdate(encounterData));
    }
  }, [encounterData]);

  useEffect(() => {
    const formStateValue = (
      questionnaireResponse.values[0]?.value as EncounterEdit[]
    )?.[0];
    if (formStateValue) {
      setEncounter(() => ({
        ...formStateValue,
      }));
    }
  }, [questionnaireResponse]);

  const handleUpdateEncounter = (updates: Partial<EncounterEdit>) => {
    clearError();
    const newEncounter = { ...encounter, ...updates };
    if (["amb", "vr", "hh"].includes(newEncounter.encounter_class)) {
      newEncounter.hospitalization = {};
    }

    if (
      ["imp", "obsenc", "emer"].includes(encounter.encounter_class) &&
      newEncounter.status === "discharged"
    ) {
      newEncounter.hospitalization = {
        ...newEncounter.hospitalization,
        discharge_disposition:
          newEncounter.hospitalization?.discharge_disposition ??
          careConfig.defaultDischargeDisposition,
      };
    } else if ("hospitalization" in newEncounter) {
      newEncounter.hospitalization = {
        ...newEncounter.hospitalization,
        discharge_disposition:
          encounterData?.hospitalization?.discharge_disposition,
      };
    }

    // Create the full encounter request object
    const encounterRequest: EncounterEdit = {
      ...newEncounter,
    };

    // Create the response value with the encounter request
    const responseValue: ResponseValue = {
      type: "encounter",
      value: [encounterRequest],
    };

    updateQuestionnaireResponseCB(
      [responseValue],
      questionnaireResponse.question_id,
    );
  };

  if (isLoading) {
    return <div>{t("loading_encounter")}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Details */}
        <div className="space-y-2">
          <Label>{t("encounter_status")}</Label>
          <Select
            value={encounter.status}
            onValueChange={(value: EncounterStatus) =>
              handleUpdateEncounter({
                status: value,
              })
            }
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("select_status")} />
            </SelectTrigger>
            <SelectContent>
              {Object.values(EncounterStatus).map(
                (encounterStatus: EncounterStatus) => (
                  <SelectItem key={encounterStatus} value={encounterStatus}>
                    {t(`encounter_status__${encounterStatus}`)}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t("encounter_class")}</Label>
          <Select
            value={encounter.encounter_class}
            onValueChange={(value: EncounterClass) =>
              handleUpdateEncounter({
                encounter_class: value,
              })
            }
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("select_class")} />
            </SelectTrigger>
            <SelectContent>
              {careConfig.encounterClasses.map((encounterClass) => (
                <SelectItem key={encounterClass} value={encounterClass}>
                  {t(`encounter_class__${encounterClass}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t("priority")}</Label>
          <Select
            value={encounter.priority}
            onValueChange={(value: EncounterPriority) =>
              handleUpdateEncounter({
                priority: value,
              })
            }
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder={t("select_priority")} />
            </SelectTrigger>
            <SelectContent>
              {ENCOUNTER_PRIORITY.map((priority) => (
                <SelectItem key={priority} value={priority}>
                  {t(`encounter_priority__${priority}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Mark for discharge button - Show if not already discharged */}
      {encounter.encounter_class === "imp" &&
        encounter.status !== "discharged" && (
          <div className="col-span-2 border border-gray-200 rounded-lg p-2 bg-gray-50">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
              <div className="space-y-1">
                <h3 className="text-sm font-medium">
                  {t("discharge_patient")}
                </h3>
              </div>
              <DischargeConfirmationDialog
                encounter={encounter}
                onConfirm={handleUpdateEncounter}
                disabled={disabled}
              />
            </div>
          </div>
        )}

      {(encounter.status === "discharged" ||
        encounter.discharge_summary_advice) && (
        <div className="space-y-6">
          <div className="space-y-2">
            <Label>{t("discharge_summary_advice")}</Label>
            <Textarea
              defaultValue={encounter.discharge_summary_advice || ""}
              onChange={(e) => {
                handleUpdateEncounter({
                  discharge_summary_advice: e.target.value || null,
                });
              }}
              disabled={disabled}
              placeholder={t("enter_discharge_summary_advice")}
            />
          </div>
        </div>
      )}

      {/* Hospitalization Details - Only show for relevant encounter classes */}
      {["imp", "obsenc", "emer"].includes(encounter.encounter_class) && (
        <div className="col-span-2 border border-gray-200 rounded-lg p-4 space-y-4">
          <h3 className="text-lg font-semibold wrap-break-word">
            {t("hospitalization_details")}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {encounter.encounter_class === "imp" && (
              <>
                <div className="space-y-2">
                  <Label>{t("mode_of_arrival")}</Label>
                  <Select
                    value={encounter.hospitalization?.arrival_mode}
                    onValueChange={(value: EncounterArrivalMode) => {
                      if (!encounter.hospitalization) return;
                      handleUpdateEncounter({
                        hospitalization: {
                          ...encounter.hospitalization,
                          arrival_mode: value,
                          referred_by_practitioner:
                            value === "referral"
                              ? encounter.hospitalization
                                  .referred_by_practitioner
                              : undefined,
                          referred_by_facility:
                            value === "referral"
                              ? encounter.hospitalization.referred_by_facility
                              : undefined,
                        },
                      });
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("select_arrival_mode")} />
                    </SelectTrigger>
                    <SelectContent>
                      {ENCOUNTER_ARRIVAL_MODE.map((mode) => (
                        <SelectItem key={mode} value={mode}>
                          {t(`encounter_arrival_mode__${mode}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasError("hospitalization.arrival_mode") && (
                    <p className="text-red-500 text-sm">
                      {getError("hospitalization.arrival_mode")?.msg}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Arrival time</Label>
                  <Input
                    type="datetime-local"
                    value={toDateTimeLocal(
                      encounter.hospitalization?.arrival_time,
                    )}
                    onChange={(event) => {
                      if (!encounter.hospitalization) return;
                      handleUpdateEncounter({
                        hospitalization: {
                          ...encounter.hospitalization,
                          arrival_time: toISOString(event.target.value),
                        },
                      });
                    }}
                    disabled={disabled}
                  />
                  {hasError("hospitalization.arrival_time") && (
                    <p className="text-red-500 text-sm">
                      {getError("hospitalization.arrival_time")?.msg}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Admission time</Label>
                  <Input
                    type="datetime-local"
                    value={toDateTimeLocal(
                      encounter.hospitalization?.admission_time,
                    )}
                    onChange={(event) => {
                      if (!encounter.hospitalization) return;
                      handleUpdateEncounter({
                        hospitalization: {
                          ...encounter.hospitalization,
                          admission_time: toISOString(event.target.value),
                        },
                      });
                    }}
                    disabled={disabled}
                  />
                  {hasError("hospitalization.admission_time") && (
                    <p className="text-red-500 text-sm">
                      {getError("hospitalization.admission_time")?.msg}
                    </p>
                  )}
                </div>

                {encounter.hospitalization?.arrival_mode === "referral" && (
                  <>
                    <div className="space-y-2">
                      <Label>{t("referred_by_practitioner")}</Label>
                      <Input
                        value={
                          encounter.hospitalization.referred_by_practitioner ??
                          ""
                        }
                        onChange={(event) =>
                          handleUpdateEncounter({
                            hospitalization: {
                              ...encounter.hospitalization,
                              referred_by_practitioner: event.target.value,
                            },
                          })
                        }
                        disabled={disabled}
                        placeholder={t("enter_practitioner_name")}
                      />
                      {hasError("hospitalization.referred_by_practitioner") && (
                        <p className="text-red-500 text-sm">
                          {
                            getError("hospitalization.referred_by_practitioner")
                              ?.msg
                          }
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{t("referred_by_facility")}</Label>
                      <Input
                        value={
                          encounter.hospitalization.referred_by_facility ?? ""
                        }
                        onChange={(event) =>
                          handleUpdateEncounter({
                            hospitalization: {
                              ...encounter.hospitalization,
                              referred_by_facility: event.target.value,
                            },
                          })
                        }
                        disabled={disabled}
                        placeholder={t("enter_facility_name")}
                      />
                      {hasError("hospitalization.referred_by_facility") && (
                        <p className="text-red-500 text-sm">
                          {
                            getError("hospitalization.referred_by_facility")
                              ?.msg
                          }
                        </p>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {encounter.encounter_class === "imp" && (
              <>
                <div className="space-y-2">
                  <Label>Expected discharge date</Label>
                  <Input
                    type="date"
                    value={
                      encounter.hospitalization?.expected_discharge_date ?? ""
                    }
                    onChange={(event) => {
                      if (!encounter.hospitalization) return;
                      handleUpdateEncounter({
                        hospitalization: {
                          ...encounter.hospitalization,
                          expected_discharge_date:
                            event.target.value || undefined,
                        },
                      });
                    }}
                    disabled={disabled}
                  />
                </div>
              </>
            )}

            <div className="flex items-center space-x-2 overflow-x-auto">
              <Switch
                checked={encounter.hospitalization?.re_admission || false}
                onCheckedChange={(checked: boolean) => {
                  if (!encounter.hospitalization) return;
                  handleUpdateEncounter({
                    hospitalization: {
                      ...encounter.hospitalization,
                      re_admission: checked,
                    },
                  });
                }}
                disabled={disabled}
              />
              <Label>{t("readmission")}</Label>
            </div>

            <div className="space-y-2">
              <Label>{t("admit_source")}</Label>
              <Select
                value={encounter.hospitalization?.admit_source}
                onValueChange={(value: EncounterAdmitSources) => {
                  if (!encounter.hospitalization) return;
                  handleUpdateEncounter({
                    hospitalization: {
                      ...encounter.hospitalization,
                      admit_source: value,
                    },
                  });
                }}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("select_admit_source")} />
                </SelectTrigger>
                <SelectContent>
                  {ENCOUNTER_ADMIT_SOURCE.map((admitSource) => (
                    <SelectItem key={admitSource} value={admitSource}>
                      {t(`encounter_admit_sources__${admitSource}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {encounter.encounter_class === "imp" && (
              <>
                <div className="flex items-center space-x-2 pt-7">
                  <Switch
                    checked={
                      encounter.hospitalization?.medico_legal_case || false
                    }
                    onCheckedChange={(checked: boolean) => {
                      if (!encounter.hospitalization) return;
                      handleUpdateEncounter({
                        hospitalization: {
                          ...encounter.hospitalization,
                          medico_legal_case: checked,
                        },
                      });
                    }}
                    disabled={disabled}
                  />
                  <Label>Medico-Legal Case</Label>
                </div>
              </>
            )}

            {/* Show discharge disposition and date when status is discharged OR has discharge disposition */}
            {(encounter.status === "discharged" ||
              encounter.hospitalization?.discharge_disposition) && (
              <>
                <div className="space-y-2">
                  <Label>
                    {t("discharge_disposition")}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={
                      encounter.hospitalization?.discharge_disposition ??
                      careConfig.defaultDischargeDisposition
                    }
                    onValueChange={(value: EncounterDischargeDisposition) => {
                      if (!encounter.hospitalization) return;
                      handleUpdateEncounter({
                        hospitalization: {
                          ...encounter.hospitalization,
                          discharge_disposition: value,
                        },
                      });
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger
                      className={cn(
                        hasError(ENCOUNTER_FIELDS.DISCHARGE_DISPOSITION.key) &&
                          "ring-1 ring-red-500",
                      )}
                    >
                      <SelectValue
                        placeholder={t("select_discharge_disposition")}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {ENCOUNTER_DISCHARGE_DISPOSITION.map((disposition) => (
                        <SelectItem key={disposition} value={disposition}>
                          {t(`encounter_discharge_disposition__${disposition}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasError(ENCOUNTER_FIELDS.DISCHARGE_DISPOSITION.key) && (
                    <p className="text-red-500 text-sm">
                      {
                        getError(ENCOUNTER_FIELDS.DISCHARGE_DISPOSITION.key)
                          ?.msg
                      }
                    </p>
                  )}
                </div>

                {encounter.status === "discharged" && (
                  <div className="space-y-2">
                    <Label>{t("discharge_date_time")}</Label>
                    <div className="flex gap-1 flex-wrap">
                      <DatePicker
                        date={
                          encounter.period.end
                            ? new Date(encounter.period.end)
                            : new Date()
                        }
                        onChange={(newDate) => {
                          if (!newDate) return;
                          const currentDate = encounter.period.end
                            ? new Date(encounter.period.end)
                            : new Date();
                          const updatedDate = new Date(newDate);
                          updatedDate.setHours(currentDate.getHours());
                          updatedDate.setMinutes(currentDate.getMinutes());
                          handleUpdateEncounter({
                            period: {
                              ...encounter.period,
                              end: updatedDate.toISOString(),
                            },
                          });
                        }}
                        disabled={(date) => {
                          if (!encounter.period.start) return false;
                          const startDate = new Date(encounter.period.start);
                          startDate.setHours(0, 0, 0, 0);
                          return date < startDate;
                        }}
                        dateFormat="d/M/yyyy"
                        className="flex-1"
                      />
                      <Input
                        type="time"
                        className="flex-1 border-t-0 sm:border-t text-sm border-gray-200 h-9"
                        value={
                          encounter.period.end
                            ? new Date(encounter.period.end).toLocaleTimeString(
                                [],
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                },
                              )
                            : new Date().toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })
                        }
                        onChange={(e) => {
                          const [hours, minutes] = e.target.value
                            .split(":")
                            .map(Number);
                          if (isNaN(hours) || isNaN(minutes)) return;
                          const updatedDate = new Date(
                            encounter.period.end || new Date(),
                          );
                          updatedDate.setHours(hours);
                          updatedDate.setMinutes(minutes);
                          handleUpdateEncounter({
                            period: {
                              ...encounter.period,
                              end: updatedDate.toISOString(),
                            },
                          });
                        }}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="space-y-2">
              <Label>{t("diet_preference")}</Label>
              <Select
                value={encounter.hospitalization?.diet_preference}
                onValueChange={(value: EncounterDietPreference) => {
                  if (!encounter.hospitalization) return;
                  handleUpdateEncounter({
                    hospitalization: {
                      ...encounter.hospitalization,
                      diet_preference: value,
                    },
                  });
                }}
                disabled={disabled}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("select_diet_preference")} />
                </SelectTrigger>
                <SelectContent>
                  {ENCOUNTER_DIET_PREFERENCE.map((dietPreference) => (
                    <SelectItem key={dietPreference} value={dietPreference}>
                      {t(`encounter_diet_preference__${dietPreference}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
